/**
 * SETUP pure helpers — adding / editing the shoot's aircraft and cameras (Plan and the live "+"
 * sheet share these).
 *
 * Aircraft roles in the UI: Subject · Chase · Watch  ↔  'subject' · 'photo_ship' (legacy 'chase') · 'other'.
 * Camera types in the UI:  Manned · Static · Aerial  ↔  'crew' · 'static' (legacy 'manual') · 'aircraft'.
 */
import { isZoom, SENSOR_PRESETS, type Aircraft, type AircraftRole, type Camera, type CameraMount, type Id, type LocationKind, type MissionAirportRole } from '@apos/shared';
import type { AircraftInput, CameraInput } from '../../lib/api';
import { safeGet, safeSet } from '../../lib/prefs';

// ───────────── aircraft ─────────────

export type ShootRole = 'subject' | 'chase' | 'watch';

export const ROLE_OPTIONS: ReadonlyArray<{ value: ShootRole; label: string }> = [
  { value: 'subject', label: 'Subject' },
  { value: 'chase', label: 'Chase' },
  { value: 'watch', label: 'Watch' },
];

/** UI label for any stored role (legacy 'chase' reads as Chase). */
export const ROLE_LABEL: Record<AircraftRole, string> = { subject: 'Subject', photo_ship: 'Chase', chase: 'Chase', other: 'Watch' };

export function shootRole(r: AircraftRole): ShootRole {
  return r === 'subject' ? 'subject' : r === 'other' ? 'watch' : 'chase';
}

/** Stored role for a UI role; keeps a legacy 'chase' as it is. */
export function storedRole(r: ShootRole, current?: AircraftRole): AircraftRole {
  if (r === 'subject') return 'subject';
  if (r === 'watch') return 'other';
  return current === 'chase' ? 'chase' : 'photo_ship';
}

/** Server defaults by role (apps/server routes/config.ts AIRCRAFT_COLORS). */
export const ROLE_COLORS: Record<AircraftRole, string> = { subject: '#FFFFFF', photo_ship: '#B69CFF', chase: '#D9D9D9', other: '#BFBFBF' };
const DEFAULT_COLORS = new Set(Object.values(ROLE_COLORS));

/** First aircraft is the Subject, then a Chase, then everything else is Watch. */
export function defaultRole(aircraft: ReadonlyArray<Pick<Aircraft, 'role'>>): AircraftRole {
  if (!aircraft.some((a) => a.role === 'subject')) return 'subject';
  if (!aircraft.some((a) => a.role === 'photo_ship' || a.role === 'chase')) return 'photo_ship';
  return 'other';
}

export function blankAircraft(existing: ReadonlyArray<Pick<Aircraft, 'role'>>): AircraftInput {
  const base: AircraftInput = {
    label: '',
    registration: null,
    icao24: null,
    flightAwareIdent: null,
    typeDesignator: null,
    role: 'other',
    trackingPriority: 'normal',
    color: ROLE_COLORS.other,
    provider: 'public',
    wingspanM: null,
    sourceUserId: null,
  };
  return withRole(base, defaultRole(existing), true);
}

/**
 * Change the role. The colour follows the role unless it was customised; for a new aircraft the
 * type and wingspan stay unspecified until entered by the operator.
 */
export function withRole(f: AircraftInput, role: AircraftRole, _isNew: boolean): AircraftInput {
  const next: AircraftInput = { ...f, role };
  if (DEFAULT_COLORS.has(f.color.toUpperCase())) next.color = ROLE_COLORS[role];
  return next;
}

/** "n 555vx" → "N555VX"; empty → null. */
export function normalizeRegistration(s: string): string | null {
  const r = s.toUpperCase().replace(/\s+/g, '');
  return r || null;
}

export function validateAircraft(f: AircraftInput): string | null {
  if (!f.label.trim() && !f.registration) return 'Enter a label or registration.';
  if ((f.provider === 'adsb' || f.provider === 'public') && !f.icao24 && !f.registration) return 'Public ADS-B needs a registration or an ICAO24 hex address.';
  if (f.provider === 'crew_device' && !f.sourceUserId) return 'Pick the crew member whose phone is on board.';
  if (f.provider === 'flightaware' && !f.flightAwareIdent && !f.registration) return 'FlightAware needs an ident or a registration.';
  return null;
}

const AIRCRAFT_FIELDS = ['label', 'registration', 'icao24', 'flightAwareIdent', 'typeDesignator', 'role', 'trackingPriority', 'color', 'provider', 'wingspanM', 'sourceUserId'] as const;

/** Body to save (input fields only): label defaults to the registration; hex lower-cased. */
export function aircraftBody(f: AircraftInput): AircraftInput {
  const out = Object.fromEntries(AIRCRAFT_FIELDS.map((k) => [k, f[k] ?? null])) as unknown as AircraftInput;
  out.label = (f.label.trim() || f.registration || '').toUpperCase();
  out.icao24 = f.icao24?.trim().toLowerCase() || null;
  out.trackingPriority = f.trackingPriority ?? 'normal';
  return out;
}

/** Changed input fields only (the server rejects unknown keys such as id / createdAt). */
export function aircraftPatch(orig: Aircraft, body: AircraftInput): Partial<AircraftInput> {
  const out: Partial<AircraftInput> = {};
  for (const k of AIRCRAFT_FIELDS) if (JSON.stringify(orig[k] ?? null) !== JSON.stringify(body[k] ?? null)) (out as Record<string, unknown>)[k] = body[k];
  return out;
}

// ───────────── cameras ─────────────

export type CameraType = 'manned' | 'static' | 'aerial';

export const CAMERA_TYPE_OPTIONS: ReadonlyArray<{ value: CameraType; label: string }> = [
  { value: 'manned', label: 'Manned' },
  { value: 'static', label: 'Static' },
  { value: 'aerial', label: 'Aerial' },
];

export const CAMERA_TYPE_LABEL: Record<CameraMount, string> = { crew: 'Manned', static: 'Static', manual: 'Static', aircraft: 'Aerial' };

export function cameraType(m: CameraMount): CameraType {
  return m === 'crew' ? 'manned' : m === 'aircraft' ? 'aerial' : 'static';
}

export function mountFor(t: CameraType, current?: CameraMount): CameraMount {
  if (t === 'manned') return 'crew';
  if (t === 'aerial') return 'aircraft';
  return current === 'manual' ? 'manual' : 'static';
}

/** The Chase aircraft (first photo ship / legacy chase), else null. */
export function chaseAircraftId(aircraft: ReadonlyArray<Pick<Aircraft, 'id' | 'role'>>): Id | null {
  return aircraft.find((a) => a.role === 'photo_ship' || a.role === 'chase')?.id ?? null;
}

/** Body / lens / focal / sensor last used on this device — prefills the next static camera. */
export interface LastCamera {
  body: string | null;
  lens: string | null;
  focalLengthMm: number | null;
  sensorWidthMm: number | null;
  sensorHeightMm: number | null;
  /** Zoom range (round 4); absent in older saved values. */
  focalMinMm?: number | null;
  focalMaxMm?: number | null;
}

export const LAST_CAMERA_KEY = 'apos.setup.lastCamera.v1';

export function readLastCamera(): LastCamera | null {
  const raw = safeGet(LAST_CAMERA_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<LastCamera>;
    const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null);
    const str = (x: unknown) => (typeof x === 'string' && x.trim() ? x : null);
    const out: LastCamera = { body: str(v.body), lens: str(v.lens), focalLengthMm: num(v.focalLengthMm), sensorWidthMm: num(v.sensorWidthMm), sensorHeightMm: num(v.sensorHeightMm) };
    const lo = num(v.focalMinMm);
    const hi = num(v.focalMaxMm);
    if (lo && hi && lo < hi) Object.assign(out, { focalMinMm: lo, focalMaxMm: hi });
    return out.focalLengthMm || out.sensorWidthMm || out.body ? out : null;
  } catch {
    return null;
  }
}

export function rememberCamera(c: Pick<CameraInput, 'body' | 'lens' | 'focalLengthMm' | 'sensorWidthMm' | 'sensorHeightMm' | 'focalMinMm' | 'focalMaxMm'>): void {
  if (!c.focalLengthMm && !c.sensorWidthMm && !c.body) return;
  const v: LastCamera = { body: c.body, lens: c.lens, focalLengthMm: c.focalLengthMm, sensorWidthMm: c.sensorWidthMm, sensorHeightMm: c.sensorHeightMm };
  if (isZoom(c)) Object.assign(v, { focalMinMm: c.focalMinMm, focalMaxMm: c.focalMaxMm });
  safeSet(LAST_CAMERA_KEY, JSON.stringify(v));
}

export interface CameraCtx {
  meUserId: Id;
  aircraft: ReadonlyArray<Pick<Aircraft, 'id' | 'role'>>;
  last: LastCamera | null;
}

/** Fill what the chosen type needs: Manned → me as operator, Aerial → the Chase, Static → last lens. */
export function withCameraType(f: CameraInput, t: CameraType, ctx: CameraCtx): CameraInput {
  const next: CameraInput = { ...f, mount: mountFor(t, f.mount) };
  if (t === 'manned' && !next.operatorUserId && !next.operatorName?.trim()) next.operatorUserId = ctx.meUserId;
  if (t === 'aerial' && !next.aircraftId) next.aircraftId = chaseAircraftId(ctx.aircraft) ?? ctx.aircraft[0]?.id ?? null;
  if (t === 'static' && ctx.last && !next.focalLengthMm && !next.sensorWidthMm) {
    next.body = next.body ?? ctx.last.body;
    next.lens = next.lens ?? ctx.last.lens;
    next.focalLengthMm = ctx.last.focalLengthMm;
    next.focalMinMm = ctx.last.focalMinMm ?? null;
    next.focalMaxMm = ctx.last.focalMaxMm ?? null;
    next.sensorWidthMm = ctx.last.sensorWidthMm;
    next.sensorHeightMm = ctx.last.sensorHeightMm;
  }
  return next;
}

export function blankCamera(label: string, t: CameraType, ctx: CameraCtx): CameraInput {
  const base: CameraInput = {
    label,
    body: null,
    lens: null,
    focalLengthMm: null,
    focalMinMm: null,
    focalMaxMm: null,
    sensorWidthMm: null,
    sensorHeightMm: null,
    mount: 'static',
    operatorUserId: null,
    operatorName: null,
    locationId: null,
    aircraftId: null,
    position: null,
    headingDeg: null,
    tiltDeg: null,
    recording: false,
    notes: '',
  };
  return withCameraType(base, t, ctx);
}

/** A static camera's field of view needs the way it faces. */
export const FACING_REQUIRED_MSG = 'Set which way the camera faces: point your phone (“Use my phone here”), choose “Face toward”, or type Facing (°).';

export const SENSOR_REQUIRED_MSG = 'Enter the actual focal length and choose or enter this camera’s sensor size.';
export const ZOOM_RANGE_MSG = 'A zoom needs its wide and long ends (wide shorter than long), e.g. 70 and 200.';

/** Prime or zoom, from the lens fields (a half-typed range still reads as zoom while editing). */
export type LensKind = 'prime' | 'zoom';
export function lensKind(c: Pick<CameraInput, 'focalMinMm' | 'focalMaxMm'>): LensKind {
  return c.focalMinMm != null || c.focalMaxMm != null ? 'zoom' : 'prime';
}

/**
 * What stops a save. Focal + sensor are required for Static only (its field of view is the point);
 * `placeOnMap` saves a static camera that will be placed by tapping the map next.
 */
export function validateCamera(f: CameraInput, opts: { placeOnMap?: boolean } = {}): string | null {
  const t = cameraType(f.mount);
  if (t === 'manned' && !f.operatorUserId && !f.operatorName?.trim()) return 'Choose who runs this camera, or type the name of someone without the app.';
  if (t === 'aerial' && !f.aircraftId) return 'Choose the aircraft this camera is on.';
  if (lensKind(f) === 'zoom' && !isZoom(f)) return ZOOM_RANGE_MSG;
  if (t === 'static') {
    if (!(f.focalLengthMm || isZoom(f)) || !f.sensorWidthMm || !f.sensorHeightMm) return SENSOR_REQUIRED_MSG;
    if (!opts.placeOnMap && !f.position) return 'Set where the camera is: “Use my phone here” at the camera, or pick it on the map or at a place.';
    if (!opts.placeOnMap && f.headingDeg === null) return FACING_REQUIRED_MSG;
  }
  return null;
}

/**
 * Body to save: drop what the type does not use (a Manned camera's stale fixed spot, etc.). A Manned
 * camera run by someone without the app keeps its spot (it has no live position of its own); one
 * run by an app user drops the typed name.
 */
export function cameraBody(f: CameraInput, fallbackLabel: string): CameraInput {
  const t = cameraType(f.mount);
  const noApp = t === 'manned' && !f.operatorUserId;
  const name = f.operatorName?.trim() || null;
  return {
    ...f,
    label: f.label.trim().toUpperCase() || fallbackLabel,
    aircraftId: t === 'aerial' ? f.aircraftId : null,
    position: t === 'static' || noApp ? f.position : null,
    locationId: t === 'static' || noApp ? (f.locationId ?? null) : null,
    operatorName: t === 'manned' && f.operatorUserId ? null : name,
    // a zoom also stores its long end as focalLengthMm (old readers know only primes)
    ...(isZoom(f) ? { focalLengthMm: f.focalMaxMm! } : { focalMinMm: null, focalMaxMm: null }),
  };
}

/** Fields a crew member may change on a camera they operate (server OPERATOR_FIELDS). */
export const OPERATOR_FIELDS = ['recording', 'headingDeg', 'focalLengthMm', 'focalMinMm', 'focalMaxMm', 'position'] as const;

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Changed fields only; an operator who is not a producer sends only what they may change. */
export function cameraPatch(orig: Camera, f: CameraInput, canEditAll: boolean): Partial<CameraInput> {
  const out: Partial<CameraInput> = {};
  const keys = (canEditAll ? Object.keys(f) : [...OPERATOR_FIELDS]) as Array<keyof CameraInput>;
  for (const k of keys) {
    if (!same(orig[k as keyof Camera], f[k])) (out as Record<string, unknown>)[k] = f[k];
  }
  return out;
}

export function sensorPresetId(c: Pick<Camera, 'sensorWidthMm' | 'sensorHeightMm'>): string {
  return SENSOR_PRESETS.find((p) => p.widthMm === c.sensorWidthMm && p.heightMm === c.sensorHeightMm)?.id ?? (c.sensorWidthMm ? 'custom' : '');
}

// ───────────── heading ─────────────

export const normDeg = (d: number) => ((Math.round(d) % 360) + 360) % 360;

export function nudgeHeading(h: number | null, delta: number): number {
  return normDeg((h ?? 0) + delta);
}

/** Typed heading → 0–359, or null when empty / not a number. */
export function parseHeading(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t.replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? normDeg(n) : null;
}

/** "247° ±5°" (accuracy when the compass reports it). */
export function aimText(headingDeg: number | null, accuracyDeg: number | null): string {
  if (headingDeg === null) return '—°';
  const h = `${String(normDeg(headingDeg)).padStart(3, '0')}°`;
  return accuracyDeg != null && accuracyDeg >= 0 ? `${h} ±${Math.round(accuracyDeg)}°` : h;
}

// ───────────── places ─────────────

/**
 * "Add place" types. Airports are mission airports (METARs, departures); the rest are map places:
 * Camera spot → 'camera_site', Team spot / staging → 'staging', Other → any remaining kind.
 */
export type PlaceType = 'airport' | 'camera' | 'team' | 'other';

export const PLACE_TYPE_OPTIONS: ReadonlyArray<{ value: PlaceType; label: string }> = [
  { value: 'airport', label: 'Airport' },
  { value: 'camera', label: 'Camera spot' },
  { value: 'team', label: 'Team spot' },
  { value: 'other', label: 'Other' },
];

export function placeTypeOf(kind: LocationKind): Exclude<PlaceType, 'airport'> {
  return kind === 'camera_site' ? 'camera' : kind === 'staging' ? 'team' : 'other';
}

export function kindForPlaceType(t: Exclude<PlaceType, 'airport'>, otherKind: LocationKind = 'poi'): LocationKind {
  return t === 'camera' ? 'camera_site' : t === 'team' ? 'staging' : otherKind;
}

/** Kinds offered under "Other". */
export const OTHER_PLACE_KINDS: LocationKind[] = ['poi', 'basecamp', 'parking', 'landing_zone', 'hazard'];

/** Default name for a new place of this kind: "CAM SPOT 2", "TEAM 1", "BASECAMP". */
export function defaultPlaceName(kind: LocationKind, existing: readonly string[]): string {
  const base = kind === 'camera_site' ? 'CAM SPOT' : kind === 'staging' ? 'TEAM' : kind === 'poi' ? 'PLACE' : kind.replace(/_/g, ' ').toUpperCase();
  const taken = new Set(existing.map((n) => n.toUpperCase()));
  for (let i = 1; i < 100; i++) if (!taken.has(`${base} ${i}`)) return `${base} ${i}`;
  return base;
}

/**
 * Airport roles in plain words. The stored roles stay 'primary' | 'alternate' | 'nearby':
 *  "We're at"        → the first is 'primary' (weather, "taking off at"), more are 'nearby';
 *  "Flies from / to" → 'alternate' (where the Subject / Chase departs or lands).
 */
export type AirportUse = 'at' | 'fromto';

export const AIRPORT_USE_OPTIONS: ReadonlyArray<{ value: AirportUse; label: string }> = [
  { value: 'at', label: 'We’re at' },
  { value: 'fromto', label: 'Flies from / to' },
];

export const AIRPORT_ROLE_LABEL: Record<MissionAirportRole, string> = { primary: 'We’re at · main', nearby: 'We’re at', alternate: 'Flies from / to' };

export function airportRoleFor(use: AirportUse, current: ReadonlyArray<{ role: MissionAirportRole }>): MissionAirportRole {
  if (use === 'fromto') return 'alternate';
  return current.some((a) => a.role === 'primary') ? 'nearby' : 'primary';
}

/** Add an airport (no duplicates); a new "we're at" becomes the main one if there is none. */
export function withAirport(
  current: ReadonlyArray<{ icao: string; role: MissionAirportRole }>,
  icao: string,
  use: AirportUse,
): Array<{ icao: string; role: MissionAirportRole }> {
  const list = current.map((a) => ({ icao: a.icao, role: a.role }));
  const i = list.findIndex((a) => a.icao === icao);
  if (i >= 0) {
    if (use === 'fromto') list[i] = { icao, role: 'alternate' };
    else if (list[i]!.role === 'alternate') list[i] = { icao, role: airportRoleFor('at', list.filter((_, j) => j !== i)) };
    return list;
  }
  return [...list, { icao, role: airportRoleFor(use, list) }];
}

/** Remove an airport; if it was the main one, the next "we're at" airport becomes main. */
export function withoutAirport(current: ReadonlyArray<{ icao: string; role: MissionAirportRole }>, icao: string): Array<{ icao: string; role: MissionAirportRole }> {
  const list = current.filter((a) => a.icao !== icao).map((a) => ({ icao: a.icao, role: a.role }));
  if (!list.some((a) => a.role === 'primary')) {
    const next = list.find((a) => a.role === 'nearby');
    if (next) next.role = 'primary';
  }
  return list;
}

// ───────────── who runs a camera ─────────────

/** "Kofi (photographer)" when nobody with the app runs it; else the member's callsign / name. */
export function operatorLabel(
  c: Pick<Camera, 'operatorUserId' | 'operatorName'>,
  members: ReadonlyArray<{ userId: Id; callsign: string; displayName: string }>,
): string | null {
  const m = c.operatorUserId ? members.find((x) => x.userId === c.operatorUserId) : null;
  if (m) return m.callsign || m.displayName;
  return c.operatorName?.trim() || null;
}

/** People on the shoot without the app: operator names on cameras (unique, in camera order). */
export function peopleWithoutApp(cameras: ReadonlyArray<Pick<Camera, 'operatorName' | 'operatorUserId' | 'label'>>): Array<{ name: string; cameras: string[] }> {
  const out = new Map<string, { name: string; cameras: string[] }>();
  for (const c of cameras) {
    const n = c.operatorName?.trim();
    if (!n || c.operatorUserId) continue;
    const key = n.toLowerCase();
    if (!out.has(key)) out.set(key, { name: n, cameras: [] });
    out.get(key)!.cameras.push(c.label);
  }
  return [...out.values()];
}

// ───────────── new shoot ─────────────

/** "N555VX · Wed 30 Sep" — or "Shoot · Wed 30 Sep" without a subject. */
export function defaultMissionName(subject: string | null, date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const W = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = Number.isFinite(d.getTime()) ? `${W[d.getUTCDay()]} ${d.getUTCDate()} ${M[d.getUTCMonth()]}` : date;
  return `${subject || 'Shoot'} · ${day}`;
}

/**
 * Coordinates pasted from a maps app: "36.6628, -121.6064", "36.6628 -121.6064", "36.66° N, 121.60° W"
 * or a maps link containing "@36.66,-121.60". Null when it is not a valid lat/lon pair.
 */
export function parseLatLon(text: string): { lat: number; lon: number } | null {
  const s = text.trim();
  const at = /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/.exec(s);
  const m = at ?? /^(-?\d+(?:\.\d+)?)\s*°?\s*([NS])?\s*[,;\s]\s*(-?\d+(?:\.\d+)?)\s*°?\s*([EW])?$/i.exec(s);
  if (!m) return null;
  let lat = Number(m[1]);
  let lon = Number(at ? m[2] : m[3]);
  if (!at) {
    if (m[2]?.toUpperCase() === 'S') lat = -Math.abs(lat);
    if (m[4]?.toUpperCase() === 'W') lon = -Math.abs(lon);
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}
